import React from "react";
import ConfirmationModal from "../../../components/ConfirmationModal";
import CompanyManagementDetailsModal from "../component/company_management_details_modal";
import CompanyManagementFilters from "../component/company_management_filters";
import CompanyManagementSummaryCards from "../component/company_management_summary_cards";
import CompanyManagementTable from "../component/company_management_table";
import {
  getCompanyMeta,
  useCompanyManagementViewModel,
} from "../view_model/company_management_viewmodel";

const CompanyManagement: React.FC = () => {
  const {
    state,
    setSearchTerm,
    applyFilters,
    changeStatusFilter,
    changeTierFilter,
    setPage,
    changePageSize,
    openDetails,
    closeDetails,
    requestCompanyStatusChange,
    cancelCompanyStatusChange,
    confirmCompanyStatusChange,
  } = useCompanyManagementViewModel();

  const statusChangeCompanyMeta = state.statusChangeCompany
    ? getCompanyMeta(state.statusChangeCompany)
    : null;
  const statusChangeShouldActivate =
    statusChangeCompanyMeta?.is_active === false;
  const statusChangeAction = statusChangeShouldActivate
    ? "Activate"
    : "Deactivate";
  const statusChangeCompanyName =
    statusChangeCompanyMeta?.name ||
    state.statusChangeCompany?.company_id ||
    "this company";
  const statusChangeMessage = statusChangeShouldActivate
    ? `Are you sure you want to activate ${statusChangeCompanyName}? Company users will regain access.`
    : `Are you sure you want to deactivate ${statusChangeCompanyName}? Company users will lose access while the company is inactive.`;

  if (state.loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h1 className="mb-6 text-2xl font-bold text-gray-900">
          Company Management
        </h1>
        <div className="rounded-lg bg-white p-6 shadow">
          Loading companies...
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          Company Management
        </h1>
        <div className="text-sm text-gray-500">{state.total} companies</div>
      </div>

      {state.error ? (
        <div className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {state.error}
        </div>
      ) : null}

      {state.statusMessage ? (
        <div className="mb-6 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {state.statusMessage}
        </div>
      ) : null}

      <CompanyManagementSummaryCards
        total={state.total}
        summary={state.summary}
      />

      <CompanyManagementFilters
        searchTerm={state.searchTerm}
        selectedStatus={state.selectedStatus}
        selectedTier={state.selectedTier}
        onSearchTermChange={setSearchTerm}
        onApplyFilters={applyFilters}
        onStatusChange={changeStatusFilter}
        onTierChange={changeTierFilter}
      />

      <CompanyManagementTable
        subscriptions={state.subscriptions}
        total={state.total}
        page={state.page}
        pageSize={state.pageSize}
        submitting={state.submitting}
        onPageChange={setPage}
        onRowsPerPageChange={changePageSize}
        onViewDetails={openDetails}
        onStatusChange={requestCompanyStatusChange}
      />

      {state.showDetails && state.selectedCompany ? (
        <CompanyManagementDetailsModal
          subscription={state.selectedCompany}
          onClose={closeDetails}
        />
      ) : null}

      <ConfirmationModal
        isOpen={Boolean(state.statusChangeCompany)}
        onClose={cancelCompanyStatusChange}
        onConfirm={confirmCompanyStatusChange}
        title={`${statusChangeAction} Company`}
        message={statusChangeMessage}
        confirmText={statusChangeAction}
        type={statusChangeShouldActivate ? "success" : "danger"}
        loading={state.submitting}
      />
    </div>
  );
};

export default CompanyManagement;
